/*!
 * FPU Navbar Breakpoint — Webflows Umklapppunkt für die Navbar hochziehen.
 * FlowPowerUser Attribute, Schema: fpu-navbar-*
 * Version: 1.2.1
 *
 * ── Grundgerüst ──────────────────────────────────────────────────────────
 *   fpu-navbar-element="navbar"    Aktivierung, auf der nativen Webflow-Navbar
 *                                  (dem Element mit .w-nav). Sitzt das Attribut
 *                                  versehentlich innen oder außen, wird die
 *                                  nächstgelegene .w-nav gesucht.
 *   fpu-navbar-breakpoint="1100"   Breite in Pixeln, BIS zu der das Burger-Menü
 *                                  aktiv sein soll. Erlaubt sind 320–2560.
 *
 * ── Optionen ─────────────────────────────────────────────────────────────
 *   fpu-navbar-styling="tablet"    Bis zu welchem Webflow-Breakpoint hinunter
 *                                  das Styling geholt wird. Standard ist
 *                                  "tablet" — so ist es in fast jedem Projekt
 *                                  gebaut.
 *                                    tablet             → 991
 *                                    mobile-landscape   → 991 und 767
 *                                    mobile             → 991, 767 und 479
 *                                  Die Breakpoints stapeln sich, deshalb
 *                                  kommen die darüberliegenden immer mit:
 *                                  Wer nur die 479er Ebene nähme, bekäme
 *                                  Desktop-Grundlage mit Mobile-Feinschliff
 *                                  — und ein Menü, das nicht umklappt.
 *   fpu-navbar-logging="true"      genau eine Init-Zeile in der Konsole
 *
 * ── Was die Lösung tut ───────────────────────────────────────────────────
 * Webflow kann das Burger-Menü nur an seinen eigenen Breakpoints umschalten.
 * Wer es früher haben will — etwa schon ab 1100 px, also mitten im
 * Desktop-Bereich — hat keinen Schalter dafür.
 *
 * Diese Lösung erfindet dafür KEIN eigenes Styling. Sie nimmt das, was im
 * Projekt ohnehin schon steht:
 *
 *   1. In jedem lesbaren Stylesheet die Media Query des gewählten
 *      Webflow-Breakpoints suchen — auch in @import und in Gruppen.
 *   2. JEDE Regel darin unverändert übernehmen, mit allen Eigenschaften,
 *      nicht nur `display`.
 *   3. Sie in dieselbe Media Query schreiben, nur mit dem Wunschwert
 *      anstelle des Webflow-Werts. Zusätzliche Bedingungen der Query
 *      (Orientierung, min-width, print) bleiben dabei erhalten.
 *
 * Was im Designer für Tablet gestylt wurde, gilt damit schon ab dem
 * Wunschwert: Hintergrund, Abstände, Schriftgrößen, Positionen, alles.
 *
 * Bewusst wird NICHT vorab geprüft, ob eine Regel gerade ein Element in der
 * Navbar trifft. Genau daran ging bisher Styling verloren: Zustände wie
 * .w--open, das Overlay des offenen Menüs, :hover, :focus, später
 * nachgeladene CMS-Elemente — nichts davon ist beim Laden der Seite im DOM
 * zu sehen, wäre also aussortiert worden. Stattdessen begrenzt jeder Selektor
 * sich selbst auf diese eine Navbar (siehe `selektorBegrenzen`); Regeln, die
 * dort nichts treffen, laufen schlicht ins Leere. Die Begrenzung erhöht
 * zugleich die Spezifität — deshalb kommt die Lösung ohne `!important` aus.
 *
 * ── Grenzen ──────────────────────────────────────────────────────────────
 * · Nur nach oben. Ein Wert unterhalb des Quell-Breakpoints wird abgelehnt:
 *   dafür gibt es Webflows eigene Einstellung am Navbar-Element.
 * · Stylesheets fremder Herkunft ohne CORS-Freigabe kann kein Browser
 *   auslesen. Webflows eigenes Projekt-CSS ist lesbar, darum geht es hier;
 *   Regeln aus fremden Quellen bleiben unberücksichtigt.
 * · Gruppen innerhalb der Quell-Query (@supports, @layer, @container)
 *   werden mitsamt ihrer Bedingung übernommen.
 * · @keyframes und @font-face werden nicht dupliziert — sie gelten ohnehin
 *   global, ein zweiter Satz unter gleichem Namen würde nur kollidieren.
 * · Verschachtelte Regeln nach CSS-Nesting (`.a { .b { … } }`) werden mit
 *   ihren eigenen Deklarationen übernommen, ihre Kinder nicht: deren `&`
 *   ließe sich nicht sinnvoll umschreiben. Webflow erzeugt kein Nesting.
 * · Die Kopie gilt nur zwischen Quell- und Wunsch-Breakpoint. Darunter
 *   bleibt Webflows eigene Staffelung unangetastet — sonst schlüge das
 *   Tablet-Styling auf dem Handy das Mobile-Styling.
 * · Von :root, html und body kommen nur die Custom Properties mit. Alles
 *   andere dort gehört der Seite, nicht der Navbar.
 * · Setzt eine Webflow-Interaction einen Wert direkt am Element (inline),
 *   gewinnt der — wie gegen jedes andere CSS auch.
 * · Kopiert wird der Stand beim Laden der Seite. Kommt danach noch ein
 *   Stylesheet dazu, wird beim Load-Ereignis einmal neu eingesammelt;
 *   später hilft window.fpuNavbarBreakpoint.refresh().
 */
(() => {
  'use strict';

  const VERSION = '1.2.1';
  const PREFIX = '[FPU Navbar Breakpoint]';

  const ATTR_ELEMENT = 'fpu-navbar-element';
  const ATTR_BREAKPOINT = 'fpu-navbar-breakpoint';
  const ATTR_STYLING = 'fpu-navbar-styling';
  const ATTR_LOGGING = 'fpu-navbar-logging';
  /** Setzen wir selbst, damit die kopierten Regeln bei dieser Navbar bleiben. */
  const ATTR_UID = 'fpu-navbar-uid';
  /** Markiert unsere eigenen <style>-Blöcke — einer je Navbar. */
  const ATTR_STIL = 'fpu-navbar-stil';

  /** Webflows eigene Breakpoints. Der Name ist der, den der Designer zeigt. */
  const QUELLEN = {
    tablet: 991,
    'mobile-landscape': 767,
    mobile: 479,
  };
  const QUELLE_STANDARD = 'tablet';

  /** Kleinster und größter sinnvoller Zielwert. Alles außerhalb ist fast
   *  immer ein Tippfehler — und ein Tippfehler darf keine Navigation
   *  unerreichbar machen. */
  const MIN = 320;
  const MAX = 2560;

  // ── Reine Bausteine (ohne DOM, Grundlage des Selbsttests) ────────────────

  /**
   * Liest den Zielwert. Akzeptiert "1100", " 1100 " und "1100px".
   * Alles andere — leer, Text, Komma, außerhalb der Grenzen — ergibt null,
   * und null heißt: nichts anfassen, Webflows Punkt bleibt.
   */
  function breakpointLesen(wert) {
    if (typeof wert !== 'string') return null;
    const roh = wert.trim().toLowerCase().replace(/px$/, '').trim();
    if (!/^\d+$/.test(roh)) return null;
    const zahl = Number.parseInt(roh, 10);
    if (!Number.isFinite(zahl) || zahl < MIN || zahl > MAX) return null;
    return zahl;
  }

  /**
   * Welcher Webflow-Breakpoint liefert das Styling. Unbekannte Angaben fallen
   * auf Tablet zurück — das ist in fast jedem Projekt der richtige.
   */
  function quelleLesen(wert) {
    if (typeof wert !== 'string') return QUELLE_STANDARD;
    const roh = wert.trim().toLowerCase();
    return Object.hasOwn(QUELLEN, roh) ? roh : QUELLE_STANDARD;
  }

  /**
   * Welche Webflow-Breakpoints gehören zu dieser Auswahl — von oben nach
   * unten.
   *
   * Webflows Breakpoints stapeln sich: Bei 400 px Fensterbreite gelten 991,
   * 767 und 479 gleichzeitig. Was im Designer für Tablet gebaut wurde, gilt
   * auf dem Handy weiter; die kleineren Breakpoints verfeinern es nur.
   *
   * Wer also das Mobile-Styling hochziehen will, braucht alle drei Ebenen.
   * Nur die 479er zu nehmen ergäbe Desktop-Grundlage mit Mobile-Feinschliff
   * — und das Menü klappte gar nicht erst um, weil Webflows Umschaltregel
   * im Tablet-Breakpoint steht.
   */
  function quellenKette(quelle) {
    const grenze = QUELLEN[quelleLesen(quelle)];
    return Object.values(QUELLEN)
      .filter((breite) => breite >= grenze)
      .sort((a, b) => b - a);
  }

  /** Welche Ebene der Kette trifft diese Query — oder keine? */
  function quelleDerQuery(bedingung, kette) {
    for (const breite of kette) if (istQuellQuery(bedingung, breite)) return breite;
    return null;
  }

  /**
   * Ist das die Media Query des gesuchten Webflow-Breakpoints?
   *
   * Ein `not` davor kehrt die Bedingung um — "not screen and (max-width:991px)"
   * gilt genau OBERHALB des Breakpoints. Solche Queries dürfen nicht als
   * Quelle durchgehen, sonst zöge die Lösung das Gegenteil hoch.
   */
  function istQuellQuery(bedingung, quellBreite) {
    if (typeof bedingung !== 'string') return false;
    if (/(^|\s)not(\s|\()/i.test(bedingung)) return false;
    return new RegExp(`max-width\\s*:\\s*${quellBreite}px`).test(bedingung);
  }

  /**
   * Baut aus der gefundenen Query die Ziel-Query.
   *
   * Zwei Dinge passieren dabei. Erstens wird nur die Breite ausgetauscht,
   * alles andere bleibt stehen: Aus "screen and (max-width: 991px) and
   * (orientation: landscape)" wird "…(max-width:1100px)…and (orientation:
   * landscape)". Würde man die Query neu bauen, gälten die Regeln plötzlich
   * auch im Hochformat — breiter, als ihr Autor sie gemeint hat.
   *
   * Zweitens bekommt sie eine Untergrenze knapp oberhalb des
   * Quell-Breakpoints. Das ist wichtig: Ohne sie gälte die Kopie bis hinunter
   * zu 0 px — und weil sie durch die Begrenzung auf die Navbar eine Stufe
   * spezifischer ist als das Original, schlüge das hochgezogene
   * Tablet-Styling auf dem Handy das echte Mobile-Styling. Unterhalb des
   * Quell-Breakpoints soll ausschließlich Webflows eigene Staffelung gelten.
   *
   * Die .02 statt .00 schließt die Lücke bei gebrochenen Fensterbreiten:
   * max-width:991px endet bei 991, min-width:992px begänne erst bei 992 —
   * ein Fenster mit 991,5 px fiele zwischen beide.
   */
  function queryUmschreiben(bedingung, quellBreite, zielBreite, grenzBreite = quellBreite) {
    const untergrenze = `(min-width:${grenzBreite}.02px)`;
    const ersatz = `screen and (max-width:${zielBreite}px) and ${untergrenze}`;
    const roh = typeof bedingung === 'string' ? bedingung.trim() : '';
    if (!roh) return ersatz;

    // Eine Media Query darf eine Liste sein. Jeder Zweig braucht seine eigene
    // Untergrenze — hinge sie nur am letzten, gälte der erste wieder bis 0 px.
    // Zweige ohne den Quell-Breakpoint gehören nicht dazu und fallen weg.
    const zweige = listeTeilen(roh)
      .map((zweig) => zweig.trim())
      .filter((zweig) => istQuellQuery(zweig, quellBreite))
      .map(
        (zweig) =>
          zweig
            .replace(new RegExp(`max-width\\s*:\\s*${quellBreite}px`, 'g'), `max-width:${zielBreite}px`)
            // Eine Untergrenze aus dem Original beschreibt den alten Bereich
            // und würde den neuen ausschließen. Sie wird durch die eigene
            // ersetzt — egal, ob sie vor oder hinter der Obergrenze steht.
            .replace(/\(\s*min-width\s*:\s*[\d.]+px\s*\)\s*and\s*/gi, '')
            .replace(/\s*and\s*\(\s*min-width\s*:\s*[\d.]+px\s*\)/gi, '')
            .trim() + ` and ${untergrenze}`,
      );
    return zweige.length ? zweige.join(',') : ersatz;
  }

  /**
   * Zerlegt eine Selektor- oder Media-Query-Liste an den Kommas — aber nur
   * an denen, die wirklich Teile trennen.
   *
   * Ein schlichtes split(',') zerreißt `:is(.a, .b)`, `:not(.a, .b)` und
   * `[title="a, b"]` mitten im Selektor. Beide Bruchstücke sind danach
   * ungültig, der Browser wirft die ganze Regel weg — und genau die Regel
   * fehlt dann im Ergebnis.
   */
  function listeTeilen(selektor) {
    if (typeof selektor !== 'string') return [];
    const teile = [];
    let tiefe = 0;
    let anfuehrung = '';
    let start = 0;
    for (let i = 0; i < selektor.length; i++) {
      const z = selektor[i];
      if (z === '\\') {
        i++; // maskiertes Zeichen überspringen
        continue;
      }
      if (anfuehrung) {
        if (z === anfuehrung) anfuehrung = '';
        continue;
      }
      if (z === '"' || z === "'") anfuehrung = z;
      else if (z === '(' || z === '[') tiefe++;
      else if (z === ')' || z === ']') tiefe--;
      else if (z === ',' && tiefe === 0) {
        teile.push(selektor.slice(start, i));
        start = i + 1;
      }
    }
    teile.push(selektor.slice(start));
    return teile;
  }

  /**
   * Pseudo-Elemente am Ende eines Selektors: ::before, ::after, ::marker,
   * ::-webkit-scrollbar-thumb, ::slotted(x) — und die alte Schreibweise mit
   * einem Doppelpunkt, die Browser bis heute verstehen.
   */
  const PSEUDO_ELEMENT_ENDE =
    /(?:::[\w-]+(?:\([^()]*\))?|:(?:before|after|first-line|first-letter))+\s*$/i;

  /**
   * Hängt an jeden Teil eines Selektors die Bedingung „gehört zu dieser
   * Navbar" an.
   *
   * Angehängt statt vorangestellt, und das ist der springende Punkt: Ein
   * vorangestelltes `[uid]` würde aus `.w-nav .w-nav-menu` die Suche nach
   * einer Navbar INNERHALB der Navbar machen — die es nicht gibt. Angehängt
   * beschreibt es dagegen genau das Gemeinte: das getroffene Element ist die
   * Navbar selbst oder liegt darin.
   *
   * Ein Pseudo-Element muss dabei ganz hinten bleiben. `.x::before:is(…)`
   * ist ungültiges CSS — nach ::before darf nichts mehr folgen —, und der
   * Browser verwirft die Regel stillschweigend. Deshalb wird die Begrenzung
   * davor eingeschoben: `.x:is(…)::before`.
   */
  /** Selektoren, die im Original die ganze Seite meinen. */
  const WURZEL = /^(?::root|html|body)$/i;

  function selektorBegrenzen(selektor, uid) {
    const grenze = `:is([${ATTR_UID}="${uid}"], [${ATTR_UID}="${uid}"] *)`;
    const navbar = `[${ATTR_UID}="${uid}"]`;
    return listeTeilen(selektor)
      .map((teil) => {
        const roh = teil.trim();
        if (!roh) return '';
        // :root, html und body treffen innerhalb der Navbar nichts — die
        // Regel liefe ins Leere. Dabei stehen dort oft Custom Properties und
        // Schriftgrößen, von denen alles darin erbt. Also biegen wir sie auf
        // die Navbar selbst um: dieselbe Vererbung, nur eine Ebene tiefer.
        if (WURZEL.test(roh)) return navbar;
        const pseudo = roh.match(PSEUDO_ELEMENT_ENDE);
        if (!pseudo) return `${roh}${grenze}`;
        return `${roh.slice(0, pseudo.index)}${grenze}${pseudo[0].trim()}`;
      })
      .filter(Boolean)
      .join(',');
  }

  // ── DOM-Teil ─────────────────────────────────────────────────────────────

  let initialized = false;
  let zaehler = 0;
  const instanzen = [];

  /** Die Navbar zum markierten Element: es selbst, ein Vorfahr oder ein Kind. */
  function navbarFinden(el) {
    if (el.classList.contains('w-nav')) return el;
    return el.closest('.w-nav') || el.querySelector('.w-nav');
  }

  /**
   * Regeln, die in einer kopierten Media Query nichts zu suchen haben.
   * @keyframes und @page sehen von außen aus wie Gruppen bzw. wie normale
   * Regeln, sind aber weder das eine noch das andere: Die Kinder von
   * @keyframes sind Prozentschritte, keine Selektoren, und "@page :first"
   * würde als Selektor ":first" ungültiges CSS ergeben.
   */
  /**
   * Aus einer Regel nur die Custom Properties herausziehen.
   *
   * Gebraucht für :root, html und body: Deren Variablen vererben sich in die
   * Navbar hinein und werden dort gebraucht. Ihre übrigen Eigenschaften
   * gehören dagegen der Seite — ein `body { background: … }` auf die Navbar
   * zu legen, wäre schlicht falsch.
   */
  function nurVariablen(style) {
    let text = '';
    for (const name of style) {
      if (name.startsWith('--')) text += `${name}:${style.getPropertyValue(name)};`;
    }
    return text;
  }

  /** Besteht die Selektorliste ausschließlich aus :root, html und body? */
  function istWurzelRegel(selektor) {
    const teile = listeTeilen(selektor);
    return teile.length > 0 && teile.every((teil) => WURZEL.test(teil.trim()));
  }

  function istFremdeRegel(regel) {
    return (
      (globalThis.CSSKeyframesRule && regel instanceof globalThis.CSSKeyframesRule) ||
      (globalThis.CSSPageRule && regel instanceof globalThis.CSSPageRule) ||
      (globalThis.CSSFontFaceRule && regel instanceof globalThis.CSSFontFaceRule)
    );
  }

  /**
   * Der Kopf einer Gruppe, so wie er im Stylesheet steht — "@supports (…)",
   * "@layer basis", "@container card (…)". Wird gebraucht, um die Gruppe um
   * die kopierte Regel herum wieder aufzubauen: Ohne ihre Bedingung würde
   * eine Regel plötzlich immer gelten, auch dort, wo ihr Autor sie
   * ausdrücklich ausgeschlossen hat.
   */
  function gruppenKopf(regel) {
    // Bei @media geht es ohne cssText — das würde die ganze Gruppe
    // serialisieren, und Webflows Breakpoint-Blöcke sind groß.
    if (regel.media) return `@media ${regel.conditionText || regel.media.mediaText}`;
    const text = typeof regel.cssText === 'string' ? regel.cssText : '';
    const klammer = text.indexOf('{');
    const kopf = klammer > 0 ? text.slice(0, klammer).trim() : '';
    return kopf.startsWith('@') ? kopf : '';
  }

  /**
   * Sammelt aus allen lesbaren Stylesheets die Regeln des Quell-Breakpoints
   * und gibt sie als fertiges CSS zurück.
   *
   * Es wird nicht gefiltert: Jede Regel der Quell-Query kommt mit, auf diese
   * Navbar begrenzt. Was dort nichts trifft, kostet ein paar Bytes und tut
   * sonst nichts — was gefiltert würde, fehlte dagegen für immer.
   *
   * Die Reihenfolge bleibt erhalten — sie entscheidet bei gleichem Gewicht.
   * Deshalb wird auch nur an den zuletzt begonnenen Block angehängt und
   * nicht nach Query gruppiert: Sonst rutschte eine Regel, die im Original
   * hinten steht, vor eine, die sie überschreiben soll.
   */
  function regelnHochziehen(uid, kette, zielBreite) {
    // Die Untergrenze kommt immer vom obersten Breakpoint der Kette:
    // darunter staffelt Webflow bereits selbst, und zwar vollständig.
    const grenzBreite = kette[0];
    const bloecke = []; // [{ query, teile }] — die Reihenfolge ist die Kaskade
    const gesehen = new Set(); // gegen Doppelarbeit und zirkuläres @import
    let anzahl = 0;
    let gesperrt = 0;

    const anhaengen = (query, css) => {
      const letzter = bloecke[bloecke.length - 1];
      if (letzter && letzter.query === query) letzter.teile.push(css);
      else bloecke.push({ query, teile: [css] });
      anzahl++;
    };

    /**
     * Alles innerhalb der Quell-Query einsammeln. `huelle` sind die Köpfe der
     * Gruppen, in denen die Regel steckt — sie werden um sie herum wieder
     * aufgebaut.
     */
    const einsammeln = (regeln, query, huelle) => {
      for (const regel of regeln) {
        if (istFremdeRegel(regel)) continue;

        if (regel.selectorText && regel.style) {
          const begrenzt = selektorBegrenzen(regel.selectorText, uid);
          if (!begrenzt) continue;
          const inhalt = istWurzelRegel(regel.selectorText)
            ? nurVariablen(regel.style)
            : regel.style.cssText;
          if (!inhalt) continue;
          let css = `${begrenzt}{${inhalt}}`;
          for (let i = huelle.length - 1; i >= 0; i--) css = `${huelle[i]}{${css}}`;
          anhaengen(query, css);
          // Kinder einer Regel entstehen durch CSS-Nesting. Ihr `&` ließe
          // sich hier nicht sinnvoll umschreiben — Webflow erzeugt so etwas
          // ohnehin nicht.
          continue;
        }

        if (regel.cssRules) {
          const kopf = gruppenKopf(regel);
          einsammeln(regel.cssRules, query, kopf ? [...huelle, kopf] : huelle);
        }
      }
    };

    /**
     * `huelle` sind die Köpfe der Gruppen, in denen die gefundene Query
     * steckt. Liegt Webflows Breakpoint etwa in einem @layer, gehört die
     * Kopie in denselben Layer — sonst verschiebt sie sich in der Kaskade
     * nach vorn und überschreibt, was sie nicht überschreiben soll.
     */
    const durchsuchen = (regeln, huelle = []) => {
      for (const regel of regeln) {
        // @import — das eingebundene Blatt gehört mit durchsucht, sonst
        // fehlt alles, was der Autor ausgelagert hat. Eine Medienbedingung
        // am @import wirkt dabei wie die am <link>.
        if (regel.styleSheet) {
          if (gesehen.has(regel.styleSheet)) continue;
          gesehen.add(regel.styleSheet);
          try {
            blattVerarbeiten(regel.media?.mediaText || '', regel.styleSheet.cssRules, huelle);
          } catch {
            gesperrt++;
          }
          continue;
        }
        if (regel.media) {
          const bedingung = regel.conditionText || regel.media.mediaText;
          const getroffen = quelleDerQuery(bedingung, kette);
          if (getroffen !== null) {
            einsammeln(
              regel.cssRules,
              queryUmschreiben(bedingung, getroffen, zielBreite, grenzBreite),
              huelle,
            );
            continue;
          }
        }
        // Gruppen weiter durchsuchen — die gesuchte Query kann darin liegen.
        if (regel.cssRules && !regel.selectorText && !istFremdeRegel(regel)) {
          const kopf = gruppenKopf(regel);
          durchsuchen(regel.cssRules, kopf ? [...huelle, kopf] : huelle);
        }
      }
    };

    /**
     * Ein Stylesheet kann seine Medienbedingung schon am <link> oder am
     * <style> tragen. Steht dort der Quell-Breakpoint, hängt das ganze Blatt
     * daran — dann stehen die Regeln darin ohne eigene Media Query und wären
     * sonst nie gefunden worden.
     */
    const blattVerarbeiten = (blattMedia, regeln, huelle = []) => {
      const getroffen = quelleDerQuery(blattMedia, kette);
      if (getroffen !== null) {
        einsammeln(regeln, queryUmschreiben(blattMedia, getroffen, zielBreite, grenzBreite), huelle);
        return;
      }
      // Ein Blatt für ein anderes Medium — print, speech — darf nicht auf
      // dem Bildschirm landen.
      const roh = blattMedia.trim().toLowerCase();
      if (roh && roh !== 'all' && roh !== 'screen') return;
      durchsuchen(regeln, huelle);
    };

    for (const blatt of document.styleSheets) {
      // Abgeschaltete Blätter gelten nicht — ihre Regeln gehören auch nicht
      // in die Kopie.
      if (gesehen.has(blatt) || blatt.disabled) continue;
      gesehen.add(blatt);
      let regeln;
      try {
        regeln = blatt.cssRules;
      } catch {
        gesperrt++; // fremde Herkunft ohne CORS — für keinen Browser lesbar
        continue;
      }
      blattVerarbeiten(blatt.media?.mediaText || '', regeln);
    }

    let css = '';
    for (const { query, teile } of bloecke) css += `@media ${query}{${teile.join('')}}`;
    return { css, anzahl, gesperrt };
  }

  class NavbarBreakpoint {
    constructor(nav, zielBreite, quelle) {
      this.nav = nav;
      this.breakpoint = zielBreite;
      this.quelle = quelle;
      this.uid = String(++zaehler);
      this.nav.setAttribute(ATTR_UID, this.uid);

      this.stil = document.createElement('style');
      this.stil.setAttribute(ATTR_STIL, this.uid);
      document.head.appendChild(this.stil);

      this.aktiv = true;
      this.aufbauen();
    }

    /** Regeln neu einsammeln und den eigenen Stilblock ersetzen. */
    aufbauen() {
      const { css, anzahl, gesperrt } = regelnHochziehen(
        this.uid,
        quellenKette(this.quelle),
        this.breakpoint,
      );
      this.stil.textContent = css;
      this.regelAnzahl = anzahl;
      this.gesperrteBlaetter = gesperrt;
      this.blaetter = document.styleSheets.length;

      if (anzahl === 0) {
        console.warn(
          `${PREFIX} Bis zum ${this.quelle}-Breakpoint (${QUELLEN[this.quelle]} px) wurde kein Styling gefunden — es bleibt alles beim Alten.`,
        );
      }
    }

    destroy() {
      this.nav.removeAttribute(ATTR_UID);
      this.stil.remove();
      this.aktiv = false;
    }
  }

  function initAll() {
    if (initialized) return;
    initialized = true;

    const markiert = document.querySelectorAll(`[${ATTR_ELEMENT}="navbar"]`);
    let logging = false;

    for (const el of markiert) {
      if (el.getAttribute(ATTR_LOGGING) === 'true') logging = true;

      const nav = navbarFinden(el);
      if (!nav) {
        console.warn(`${PREFIX} Keine Webflow-Navbar (.w-nav) gefunden — Attribut sitzt falsch.`);
        continue;
      }
      if (nav.hasAttribute(ATTR_UID)) continue; // zwei Markierungen, eine Navbar

      // Werte dürfen am markierten Element ODER an der Navbar stehen — je
      // nachdem, wo der Nutzer sie gesetzt hat.
      const rohWert = el.getAttribute(ATTR_BREAKPOINT) || nav.getAttribute(ATTR_BREAKPOINT);
      const zielBreite = breakpointLesen(rohWert ?? '');
      if (zielBreite === null) {
        console.warn(
          `${PREFIX} ${ATTR_BREAKPOINT}="${rohWert ?? ''}" ist unbrauchbar — erlaubt sind ${MIN}–${MAX}. Webflows eigener Punkt bleibt.`,
        );
        continue;
      }

      const quelle = quelleLesen(el.getAttribute(ATTR_STYLING) || nav.getAttribute(ATTR_STYLING));
      // Verglichen wird mit dem obersten Breakpoint der Kette — bei
      // "mobile" ist das nicht 479, sondern 991: Von dort an aufwärts wird
      // hochgezogen, alles darunter staffelt Webflow selbst.
      const quellBreite = quellenKette(quelle)[0];

      // Nur nach oben. Nach unten kann Webflow selbst — am Navbar-Element
      // im Designer, ganz ohne Attribut.
      if (zielBreite <= quellBreite) {
        console.warn(
          `${PREFIX} ${zielBreite} px liegt nicht über dem ${quelle}-Breakpoint (${quellBreite} px). Für einen früheren Umschaltpunkt die Einstellung an der Navbar selbst nutzen.`,
        );
        continue;
      }

      instanzen.push(new NavbarBreakpoint(nav, zielBreite, quelle));
    }

    if (logging && instanzen.length > 0) {
      for (const i of instanzen) {
        console.info(
          `${PREFIX} v${VERSION} · ${i.quelle} (${QUELLEN[i.quelle]} px) → ${i.breakpoint} px, ${i.regelAnzahl} Regeln übernommen` +
            (i.gesperrteBlaetter ? `, ${i.gesperrteBlaetter} fremde Stylesheets nicht lesbar` : ''),
        );
      }
    }

    // Beim gemeinsamen Dach melden — daraufhin laufen die Rückrufe, die
    // jemand über window.FlowPowerUser.push() angemeldet hat. Defensiv
    // aufgerufen: ohne Kern (Selbsttest) passiert hier einfach nichts.
    window.__fpu?.anmelden?.('navbar-breakpoint', {
      version: VERSION,
      api: window.fpuNavbarBreakpoint,
      starten: initAll,
      abraeumen: () => window.fpuNavbarBreakpoint.destroyAll(),
    });
  }

  window.fpuNavbarBreakpoint = {
    version: VERSION,
    instanzen,
    init: initAll,
    /** Regeln neu einsammeln — nach nachgeladenem CSS oder aus der Konsole. */
    refresh() {
      for (const instanz of instanzen) if (instanz.aktiv) instanz.aufbauen();
    },
    __pure: {
      breakpointLesen,
      quelleLesen,
      istQuellQuery,
      queryUmschreiben,
      listeTeilen,
      selektorBegrenzen,
      istWurzelRegel,
      quellenKette,
      quelleDerQuery,
      QUELLEN,
      MIN,
      MAX,
    },
    destroyAll() {
      // Nur die eigenen Blöcke — eine zweite Einbindung des Skripts hat
      // ihre eigenen, und die gehen uns nichts an.
      for (const instanz of instanzen) instanz.destroy();
      instanzen.length = 0;
      initialized = false;
      zaehler = 0;
    },
  };

  // Primärpfad: Webflow-Queue. webflow.js adoptiert ein vorab existierendes
  // Array und führt die Queue NACH dem Build seiner Module aus —
  // funktioniert daher auch bei Script-Platzierung im <head>.
  (window.Webflow = window.Webflow || []).push(initAll);

  // Fallback nur für Seiten ohne webflow.js.
  const initWithoutWebflow = () => {
    if (initialized) return;
    const hasWebflowScript = !!document.querySelector('script[src*="webflow"]');
    if (!hasWebflowScript && Array.isArray(window.Webflow)) initAll();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWithoutWebflow, { once: true });
  } else {
    setTimeout(initWithoutWebflow, 0);
  }
  window.addEventListener(
    'load',
    () => {
      // Sicherheitsnetz: Queue wurde bis zum Load-Event nie adoptiert.
      if (!initialized && Array.isArray(window.Webflow)) {
        initAll();
        return;
      }
      // Kam nach dem Start noch ein Stylesheet dazu — asynchron geladenes CSS,
      // per JS eingehängte Blöcke —, fehlten genau dessen Regeln. Also neu.
      for (const instanz of instanzen) {
        if (instanz.aktiv && instanz.blaetter !== document.styleSheets.length) instanz.aufbauen();
      }
    },
    { once: true },
  );
})();
